import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import driverAPI from '../../api/driverAPI';

const PHP_RATE = (n) =>
  `${(parseFloat(n || 0) * 100).toFixed(0)}%`;

const emptyForm = {
  name: '',
  nickname: '',
  contactNumber: '',
  licenseNumber: '',
  licenseExpiry: '',
  truckColor: '',
  truckPlateCode: '',
  vanSize: '',
  description: '',
  payoutRate: '10',
  isActive: true,
};

export default function DriverMaintenancePage() {
  const [drivers, setDrivers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null); // driver._id or null

  const [form, setForm] = useState(emptyForm);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const res = await driverAPI.getAll({ limit: 200 });
      setDrivers(res.data?.data || []);
    } catch {
      setError('Failed to load drivers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDrivers(); }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return drivers;
    return drivers.filter(
      (d) =>
        d.name?.toLowerCase().includes(term) ||
        d.nickname?.toLowerCase().includes(term) ||
        d.driverCode?.toLowerCase().includes(term)
    );
  }, [drivers, searchTerm]);

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (driver) => {
    setEditing(driver._id);
    setForm({
      name:          driver.name          || '',
      nickname:      driver.nickname      || '',
      contactNumber: driver.contactNumber || '',
      licenseNumber: driver.licenseNumber || '',
      licenseExpiry: driver.licenseExpiry
        ? driver.licenseExpiry.split('T')[0]
        : '',
      truckColor:    driver.truckColor    || '',
      truckPlateCode: driver.truckPlateCode || '',
      vanSize:       driver.vanSize       || '',
      description:   driver.description   || '',
      payoutRate:    String((parseFloat(driver.payoutRate || 0.10) * 100).toFixed(0)),
      isActive:      driver.isActive !== false,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Driver name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        payoutRate: parseFloat(form.payoutRate || 10) / 100,
        licenseExpiry: form.licenseExpiry || null,
      };
      if (editing) {
        await driverAPI.update(editing, payload);
      } else {
        await driverAPI.create(payload);
      }
      await loadDrivers();
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save driver.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (driver) => {
    if (!window.confirm(`Deactivate driver "${driver.name}"? They will no longer appear in search.`)) return;
    try {
      await driverAPI.delete(driver._id);
      await loadDrivers();
    } catch {
      setError('Failed to deactivate driver.');
    }
  };

  return (
    <Layout pageTitle="Driver Maintenance">
      <div className="space-y-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search by name, nickname, or code…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-72"
          />
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            + New Driver
          </button>
        </div>

        {error && !modalOpen && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Code', 'Name', 'Nickname', 'Truck Color', 'Plate Code', 'License No.', 'License Expiry', 'Payout %', 'Status', ''].map(
                  (h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-400">No drivers found.</td>
                </tr>
              ) : (
                filtered.map((d) => (
                  <tr key={d._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{d.driverCode}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{d.name}</td>
                    <td className="px-4 py-3 text-gray-600">{d.nickname || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{d.truckColor || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{d.truckPlateCode || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{d.licenseNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {d.licenseExpiry
                        ? new Date(d.licenseExpiry).toLocaleDateString('en-PH', { dateStyle: 'medium' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{PHP_RATE(d.payoutRate)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          d.isActive !== false
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {d.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(d)}
                        className="text-teal-600 hover:text-teal-800 text-xs font-medium mr-3"
                      >
                        Edit
                      </button>
                      {d.isActive !== false && (
                        <button
                          onClick={() => handleDeactivate(d)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-gray-900">
              {editing ? 'Edit Driver' : 'New Driver'}
            </h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>
            )}

            <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MF label="Full Name *" name="name" value={form.name} onChange={handleFieldChange} required />
              <MF label="Nickname" name="nickname" value={form.nickname} onChange={handleFieldChange} placeholder="e.g. BISOY" />
              <MF label="Contact Number" name="contactNumber" value={form.contactNumber} onChange={handleFieldChange} />
              <MF label="License No." name="licenseNumber" value={form.licenseNumber} onChange={handleFieldChange} />
              <MF label="License Expiry" name="licenseExpiry" type="date" value={form.licenseExpiry} onChange={handleFieldChange} />
              <MF label="Truck Color" name="truckColor" value={form.truckColor} onChange={handleFieldChange} placeholder="e.g. PINK" />
              <MF label="Plate Code" name="truckPlateCode" value={form.truckPlateCode} onChange={handleFieldChange} placeholder="e.g. 071607" />
              <MF label="Van Size" name="vanSize" value={form.vanSize} onChange={handleFieldChange} placeholder="e.g. 40FT" />
              <MF label="Description" name="description" value={form.description} onChange={handleFieldChange} placeholder="e.g. 10-wheeler closed van" />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payout Rate (%)</label>
                <input
                  type="number"
                  name="payoutRate"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.payoutRate}
                  onChange={handleFieldChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {editing && (
                <div className="flex items-center gap-2 col-span-full">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleFieldChange}
                    className="h-4 w-4 text-teal-600 rounded"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700 cursor-pointer">Active</label>
                </div>
              )}

              <div className="col-span-full flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 font-medium"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function MF({ label, name, value, onChange, type = 'text', required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder || ''}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );
}
